import { Text, Card, CardHeader, Badge } from '@fluentui/react-components';
import { CloudRegular } from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import type { Project } from '../services/project-service';
import { getBadgeProps, CAF_PHASE_BADGE, PROJECT_STATUS_BADGE } from '../utils/badge-config';

interface DashboardPageProps {
  projects?: Project[];
  documentCounts?: { total: number; draft: number; review: number; final: number };
  onSelectProject?: (project: Project) => void;
}

export function DashboardPage({ projects = [], documentCounts, onSelectProject }: DashboardPageProps) {
  const intl = useIntl();
  const activeProjects = projects.filter(p => p.status === 'active').length;
  const completedProjects = projects.filter(p => p.status === 'complete').length;
  const totalDocs = documentCounts?.total ?? 0;
  const finalDocs = documentCounts?.final ?? 0;
  const completionRate = totalDocs > 0 ? Math.round(100 * finalDocs / totalDocs) : 0;

  return (
    <div className="page-root page-root--scrollable dashboard-page">
      <div className="page-title-bar">
        <CloudRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'dashboard.title' })}</Text>
      </div>

      <div className="dashboard-summary">
        <Card size="small" className="summary-card">
          <CardHeader
            header={<Text size={200} weight="semibold" className="text-muted">{intl.formatMessage({ id: 'dashboard.card.projects.label' })}</Text>}
            description={<Text size={600} weight="bold" className="text-brand">{projects.length}</Text>}
          />
          <div className="summary-card-detail">
            <div className="flex-row flex-wrap">
              <Badge size="small" color="success">{intl.formatMessage({ id: 'dashboard.card.projects.active' }, { count: activeProjects })}</Badge>
              <Badge size="small" appearance="outline">{intl.formatMessage({ id: 'dashboard.card.projects.complete' }, { count: completedProjects })}</Badge>
            </div>
          </div>
        </Card>

        <Card size="small" className="summary-card">
          <CardHeader
            header={<Text size={200} weight="semibold" className="text-muted">{intl.formatMessage({ id: 'dashboard.card.documents.label' })}</Text>}
            description={<Text size={600} weight="bold" className="text-brand">{totalDocs}</Text>}
          />
          <div className="summary-card-detail">
            <div className="flex-row flex-wrap">
              <Badge size="small" color="warning">{intl.formatMessage({ id: 'dashboard.card.documents.draft' }, { count: documentCounts?.draft ?? 0 })}</Badge>
              <Badge size="small" color="brand">{intl.formatMessage({ id: 'dashboard.card.documents.review' }, { count: documentCounts?.review ?? 0 })}</Badge>
              <Badge size="small" color="success">{intl.formatMessage({ id: 'dashboard.card.documents.final' }, { count: documentCounts?.final ?? 0 })}</Badge>
            </div>
          </div>
        </Card>

        <Card size="small" className="summary-card">
          <CardHeader
            header={<Text size={200} weight="semibold" className="text-muted">{intl.formatMessage({ id: 'dashboard.card.completionRate.label' })}</Text>}
            description={<Text size={600} weight="bold" className="text-success">{completionRate}%</Text>}
          />
          <div className="summary-card-detail">
            <Text size={200} className="text-muted">{intl.formatMessage({ id: 'dashboard.card.completionRate.detail' }, { finalDocs, totalDocs })}</Text>
          </div>
        </Card>

        <Card size="small" className="summary-card">
          <CardHeader
            header={<Text size={200} weight="semibold" className="text-muted">{intl.formatMessage({ id: 'dashboard.card.cafPhases.label' })}</Text>}
            description={<Text size={600} weight="bold" className="text-brand">{intl.formatMessage({ id: 'dashboard.card.cafPhases.range' })}</Text>}
          />
          <div className="summary-card-detail">
            <Text size={200} className="text-muted">{intl.formatMessage({ id: 'dashboard.card.cafPhases.detail' })}</Text>
          </div>
        </Card>
      </div>

      <div className="dashboard-section">
        <div className="section-header">
          <Text size={400} weight="semibold">{intl.formatMessage({ id: 'dashboard.recentProjects.heading' })}</Text>
        </div>
        <div className="table-scroll-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'dashboard.col.project' })}</th>
                <th>{intl.formatMessage({ id: 'dashboard.col.customer' })}</th>
                <th>{intl.formatMessage({ id: 'dashboard.col.phase' })}</th>
                <th>{intl.formatMessage({ id: 'dashboard.col.status' })}</th>
              </tr>
            </thead>
            <tbody>
              {projects.slice(0, 5).map(p => (
                <tr key={p.id} className="clickable" onClick={() => onSelectProject?.(p)}>
                  <td><Text weight="semibold">{p.name}</Text></td>
                  <td className="text-muted">{p.customerId}</td>
                  <td><Badge size="small" {...getBadgeProps(CAF_PHASE_BADGE, p.cafPhase)}>{p.cafPhase}</Badge></td>
                  <td><Badge size="small" {...getBadgeProps(PROJECT_STATUS_BADGE, p.status)}>{p.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
